import styled from 'styled-components/macro';
import { fontSizePalette, sizePalette } from 'utils/styleConstants';
import { AutocompleteStyles } from './types';

export const Container = styled.div`
  position: relative;
  width: ${sizePalette.size.full};
  :disabled {
    cursor: not-allowed;
    opacity: 0.5;
    background-color: red;
  }
`;

export const InputWrapper = styled.div<{ isOpen: boolean; small?: boolean; disabled?: boolean }>`
  display: flex;
  justify-content: space-between;
  width: ${sizePalette.size.full};
  padding-left: ${sizePalette.spacing[2]};
  height: ${({ small }) => (small ? '20px' : sizePalette.spacing.pxToRem(30))};
  font-size: ${({ theme }) => theme.fontSizePalette.xSmall};
  border-radius: ${({ small }) => (small ? sizePalette.borderRadius['2xl'] : sizePalette.borderRadius.DEFAULT)};
  background-color: ${({ theme }) =>
    theme.colorPalette.isDarkMode ? theme.colorPalette.grey.grey4 : theme.colorPalette.white};
  transition: outline-color ${sizePalette.transitionDuration[200]};
  display: flex;
  align-items: center;
  cursor: text;
  z-index: 2;
  position: relative;
  outline: ${sizePalette.borderWidth.DEFAULT} solid transparent;
  outline: ${({ small, theme }) =>
    small
      ? `${sizePalette.borderWidth.DEFAULT} solid transparent`
      : `${sizePalette.borderWidth.DEFAULT} solid ${
          theme.colorPalette.isDarkMode ? 'transparent' : theme.colorPalette.grey.grey5
        }`};
  outline-offset: 0.5px;
  &:focus-within {
    outline: ${sizePalette.borderWidth.DEFAULT} solid
      ${({ theme }) =>
        theme.colorPalette.isDarkMode ? theme.colorPalette.turquoise.accent : theme.colorPalette.turquoise.dark};
  }

  ${({ isOpen, theme }) =>
    isOpen &&
    `
     outline: ${sizePalette.borderWidth.DEFAULT} solid ${
      theme.colorPalette.isDarkMode ? theme.colorPalette.turquoise.accent : theme.colorPalette.turquoise.dark
    };

  `}
  ${({ disabled, theme }) =>
    disabled &&
    `
    cursor: default;
    background-color: ${theme.colorPalette.grey.grey1};
  `}
`;

export const TagsContainer = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  padding: ${sizePalette.spacing[0]} ${sizePalette.spacing[1]} ${sizePalette.spacing[0]} ${sizePalette.spacing[0]};
  height: 100%;
`;

export const Tag = styled.span<{ isFirst: boolean; small?: boolean; disabled?: boolean }>`
  font-size: ${({ theme, small }) => (small ? theme.fontSizePalette.xSmall : theme.fontSizePalette.small)};
  color: ${({ theme }) =>
    theme.colorPalette.isDarkMode ? theme.colorPalette.grey.grey8 : theme.colorPalette.grey.grey8};
  white-space: nowrap;
  display: inline-block;
  cursor: pointer;
  transition: color ${sizePalette.transitionDuration[200]};
  border-radius: ${sizePalette.borderRadius.sm};
  outline: none;
  &:hover {
    color: ${({ theme, disabled }) => (disabled ? theme.statusColors.error : theme.colorPalette.grey.grey8)};
    text-decoration: ${({ disabled }) => (disabled ? 'line-through' : 'none')};
  }

  &:focus {
    outline: 1px solid ${({ theme }) => theme.colorPalette.turquoise.normal};
    outline-offset: 1px;
  }
`;

export const PlusChip = styled.span<{ hasMargin: boolean; small?: boolean }>`
  font-size: ${({ theme, small }) => (small ? theme.fontSizePalette.xSmall : theme.fontSizePalette.small)};
  color: ${({ theme }) =>
    theme.colorPalette.isDarkMode ? theme.colorPalette.turquoise.accent : theme.colorPalette.turquoise.dark};
  font-weight: bold;
  cursor: pointer;
  margin-left: ${(props) => (props.hasMargin ? sizePalette.spacing[1] : '0px')};
  border-radius: ${sizePalette.borderRadius.sm};
  outline: none;

  &:focus {
    outline: 1px solid ${({ theme }) => theme.colorPalette.turquoise.normal};
  }
`;

export const Input = styled.input<{ hasValue: boolean; small?: boolean }>`
  height: 100%;
  padding: 0 ${sizePalette.spacing[2]} 0 ${sizePalette.spacing[0]};
  border: none;
  outline: none;
  flex: 1;
  font-size: ${({ theme, small }) => (small ? theme.fontSizePalette.xSmall : theme.fontSizePalette.small)};
  background-color: transparent;
  min-width: 40px;
  font-family: ${({ theme }) => theme.fontFamily};
  color: ${({ theme }) => (theme.colorPalette.isDarkMode ? theme.colorPalette.grey.grey10 : theme.colorPalette.black)};

  &::placeholder {
    color: ${({ theme }) => theme.colorPalette.grey.grey7};
  }
`;

export const Dropdown = styled.div<{ small?: boolean }>`
  position: absolute;
  top: ${sizePalette.size.full};
  left: 0;
  right: 0;
  background-color: ${({ theme }) =>
    theme.colorPalette.isDarkMode ? theme.colorPalette.grey.grey3 : theme.colorPalette.white};
  border: ${sizePalette.borderWidth.DEFAULT} solid
    ${({ theme }) =>
      theme.colorPalette.isDarkMode ? theme.colorPalette.turquoise.accent : theme.colorPalette.turquoise.dark};
  border-radius: ${sizePalette.borderRadius.DEFAULT};
  max-height: 300px;
  z-index: ${({ theme }) => theme.zIndexPalette.low};
  box-shadow: ${sizePalette.boxShadow.md};
  margin-top: ${({ small }) => (small ? sizePalette.spacing.pxToRem(10) : sizePalette.spacing.pxToRem(10))};
  overflow: hidden;
`;

export const Scroller = styled.div`
  max-height: 256px;
  overflow-y: auto;
  margin: 2px;
`;
export const Option = styled.div<{ isFocused: boolean; isSelected: boolean }>`
  padding: 6px 6px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: background-color ${sizePalette.transitionDuration[200]};
  background-color: ${({ theme, isFocused, isSelected }) =>
    isFocused
      ? theme.colorPalette.withOpacity(
          theme.colorPalette.isDarkMode ? theme.colorPalette.grey.grey9 : theme.colorPalette.turquoise.dark,
          theme.colorPalette.isDarkMode ? 0.3 : 0.3,
        )
      : isSelected
      ? theme.colorPalette.withOpacity(
          theme.colorPalette.isDarkMode ? theme.colorPalette.grey.grey9 : theme.colorPalette.turquoise.dark,
          theme.colorPalette.isDarkMode ? 0.15 : 0.15,
        )
      : 'transparent'};
  font-size: 12px;
  color: ${({ theme }) => (theme.colorPalette.isDarkMode ? theme.colorPalette.grey.grey10 : theme.colorPalette.black)};
  &:hover {
    background-color: ${({ theme }) =>
      theme.colorPalette.withOpacity(
        theme.colorPalette.isDarkMode ? theme.colorPalette.grey.grey9 : theme.colorPalette.turquoise.dark,
        theme.colorPalette.isDarkMode ? 0.3 : 0.3,
      )};
  }
`;

export const RemoveIcon = styled.span`
  color: ${({ theme }) => theme.statusColors.error};
  font-size: ${({ theme }) => theme.fontSizePalette.xSmall};
  margin-left: ${sizePalette.spacing[2]};
  font-weight: bold;
`;

export const X = styled.span`
  margin-left: auto;
  color: ${(props) =>
    props.theme.colorPalette.isDarkMode
      ? props.theme.colorPalette.turquoise.accent
      : props.theme.colorPalette.turquoise.dark};
  font-weight: ${(props) => props.theme.fontWeightPalette.bold};
`;

export const ClearButton = styled.button<{ small?: boolean }>`
  background: none;
  border: none;
  cursor: pointer;
  padding: ${sizePalette.spacing[1]};
  margin-right: ${sizePalette.spacing[1]};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${sizePalette.borderRadius.sm};
  color: ${({ theme }) =>
    theme.colorPalette.isDarkMode ? theme.colorPalette.grey.grey10 : theme.colorPalette.grey.grey6};
  font-size: ${({ small }) => (small ? '12px' : '14px')};
  min-width: 20px;
  height: ${({ small }) => (small ? '16px' : '20px')};
  outline: none;

  &:hover {
    color: ${({ theme }) => theme.statusColors.error};
  }

  &:focus {
    outline: 1px solid ${({ theme }) => theme.colorPalette.turquoise.normal};
  }

  &:active {
    transform: scale(0.95);
  }
`;

export const smallStyles: AutocompleteStyles = {
  inputWrapper: {
    borderRadius: sizePalette.borderRadius['2xl'],
    height: '20px',
  },
  dropdown: {
    paddingTop: sizePalette.spacing.pxToRem(14),
    marginTop: `-${sizePalette.spacing.pxToRem(12)}`,
  },
  input: {
    fontSize: fontSizePalette.xSmall,
  },
  tag: {
    fontSize: fontSizePalette.xSmall,
  },
};
